<template>
  <div class="row">
    <div v-if="!creating" class="col-12 col-lg-10 offset-lg-1 foo">
      <basic-portlet :title="podcast.name" :image="podcast.feed_artwork_thumbnail_url">
        <template #portlet_actions>
          <div v-if="!creating">
            <share-btn :podcast="podcast" :inline="true" />
          </div>
        </template>

        <div class="row">
          <div class="col-12">
            <b-card no-body>
              <b-tabs v-model="active_tab"
                      pills
                      card
                      align="center">
                <b-tab title="Episodes" @click="rememberTab('episodes')">
                  <episodes />
                </b-tab>
                <b-tab title="Settings" @click="rememberTab('settings')">
                  <podcast-settings-general :borderless="true" />
                  <podcast-settings-discovery :borderless="true" />
                  <podcast-settings-author-details :borderless="true" />
                  <podcast-settings-advanced :borderless="true" />
                </b-tab>
                <b-tab title="Website" @click="rememberTab('website')">
                  <website />
                </b-tab>
                <b-tab v-if="can_access_people" title="People" @click="rememberTab('people')">
                  <podcast-settings-people :borderless="true" />
                </b-tab>
                <b-tab title="Syndication" @click="rememberTab('syndication')">
                  <external-links />
                </b-tab>
                <b-tab title="Sharing" @click="rememberTab('sharing')">
                  <share-tab />
                </b-tab>
              </b-tabs>
            </b-card>
          </div>
        </div>
      </basic-portlet>
      <sticky-control-bar>
        <button type="button"
                class="btn btn-ss-danger"
                :disabled="loading"
                @click="confirmModalThen([podcast], () => awaitThenRedirect('/podcast', destroyFeeds, podcast), 'confirm-podcast-delete')">
          <loader v-if="loading" small class="mr-2" />
          <i v-else class="flaticon2-warning" />
          Delete Podcast
        </button>
        <button type="button"
                class="btn btn-ss-standout"
                :disabled="loading"
                @click="() => saveOrUpdate(true)">
          <loader v-if="loading" small class="mr-2" />
          <i v-else class="la la-save" />
          Update Podcast
        </button>
      </sticky-control-bar>
    </div>
    <div v-else class="col-12 col-lg-10 offset-lg-1">
      <basic-portlet>
        <podcast-settings-general :borderless="true" />
        <podcast-settings-discovery :borderless="true" />
        <podcast-settings-author-details :borderless="true" />
        <podcast-settings-advanced :borderless="true" />
      </basic-portlet>
      <sticky-control-bar stuck-width="95%">
        <div class="w-100 float-right">
          <button type="button"
                  class="btn btn-ss-standout float-right"
                  :disabled="loading"
                  @click="() => saveOrUpdate()">
            <loader v-if="loading" small class="mr-2" />
            <i v-else class="la la-save" />
            Create Podcast
          </button>
        </div>
      </sticky-control-bar>
    </div>
    <item-creation-modal :preselected-feed="podcast" :is-episode="true" />
    <delete-confirm-modal name="confirm-podcast-delete" :entity-type="getFeedLabel()">
      You are about to delete {{ getFeedLabel() }} "<span class="font-weight-bold">{{ staged_models?.at(0)?.name }}"</span>!
    </delete-confirm-modal>
  </div>
</template>

<script>

import { destroyFeeds } from "Lib/feeds";
import { awaitThenRedirect } from "Lib/ajax-util";
import { mapWritableState, mapActions } from 'pinia';
import { usePodcastManageStore } from "VueStore/app/podcastManage";

import {
  BCard,
  BTabs,
  BTab,
} from "bootstrap-vue";

import StickyControlBar from "VueComponents/StickyControlBar";
import SocialShareMixin from "VueMixins/SocialShareMixin";
import PodcastSettingsGeneral from "VuePartials/Feeds/PodcastSettings/General";
import PodcastSettingsDiscovery from "VuePartials/Feeds/PodcastSettings/Discovery";
import PodcastSettingsAuthorDetails from "VuePartials/Feeds/PodcastSettings/AuthorDetails";
import PodcastSettingsAdvanced from "VuePartials/Feeds/PodcastSettings/Advanced";
import PodcastSettingsPeople from "VuePartials/Feeds/PodcastSettings/People";
import ExternalLinks from "VuePartials/Feeds/PodcastSettings/ExternalLinks";
import Website from "VuePartials/Feeds/PodcastSettings/Website";
import ItemQuickCreateMixin from "VueMixins/ItemQuickCreateMixin";
import Episodes from "VuePartials/Feeds/PodcastSettings/Episodes";
import ShareBtn from "VuePartials/Feeds/PodcastSettings/ShareBtn";
import ShareTab from "VuePartials/Feeds/PodcastSettings/Share";
import DeleteConfirmModal from 'VueComponents/VJSModals/DeleteConfirmModal';

import VeeValidate from 'vee-validate';
Vue.use(VeeValidate, { inject: false });
VeeValidate.config.fieldsBagName = "veeFields";

export default {
  components: {
    ShareBtn,
    Episodes,
    BCard,
    BTabs,
    BTab,
    PodcastSettingsGeneral,
    PodcastSettingsDiscovery,
    PodcastSettingsAuthorDetails,
    PodcastSettingsAdvanced,
    PodcastSettingsPeople,
    ExternalLinks,
    StickyControlBar,
    Website,
    ShareTab,
    DeleteConfirmModal,
  },
  mixins: [ItemQuickCreateMixin, SocialShareMixin],
  /**
   * In order to validate forms inside child components we need to provide the parent instance of validator
   * Each component that needs validating will need to include the PodcastValidateMixin which references this parent
   * via injection.
   * @returns {*}
   */
  provide() {
    return { parentValidator: this.$validator };
  },
  data() {
    return {
      feed: window.feed,
      user: window.user,
    };
  },
  computed: {
    // These can be in an array, but PHPStorm doesn't detect it.
    ...mapWritableState(usePodcastManageStore, ['podcast']),
    ...mapWritableState(usePodcastManageStore, ['episodes']),
    ...mapWritableState(usePodcastManageStore, ['active_tab']),
    ...mapWritableState(usePodcastManageStore, ['creating']),
    ...mapWritableState(usePodcastManageStore, ['loading']),
    can_access_people() {
      return this.permissionsCanAccessFeature('people') && this.permissionsCanDoAction('api.people.list');
    },
  },
  beforeMount() {
    if (this.feed) {
      // Only set podcast data if we are editing.
      this.setPodcastData(this.feed);
    } else {
      this.setDefaults();
    }
    this.setActiveTab();
  },
  methods: {
    destroyFeeds,
    awaitThenRedirect,
    // These can be in an array, but PHPStorm doesn't detect it.
    ...mapActions(usePodcastManageStore, ['setPodcastData']),
    ...mapActions(usePodcastManageStore, ['setDefaults']),
    ...mapActions(usePodcastManageStore, ['validateAndSave']),
    ...mapActions(usePodcastManageStore, ['setActiveTab']),
    rememberTab(tab) {
      window.location.hash = `#${tab}`;
    },
    async saveOrUpdate(update = false) {
      this.loading = true;
      await this.validateAndSave(this.$validator, update);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~AssetsSass/_variables.scss" as *;

:deep(.nav-pills) {
  margin-bottom: 0;
  padding: 0 24em;
  flex-wrap: nowrap;

  .nav-item {
    margin: 0 !important;
    flex: 1 1 0; // grow | shrink | basis
    text-align: center;

    .nav-link.active, .nav-link:active {
      background-color: map.get($ss, 'primary') !important;
      color: white !important;
    }
  }
}
</style>
